import * as E from './errors';

export function colorHexToRGB(hex: string) {
	// Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
	const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
	hex = hex.replace(shorthandRegex, (match, r, g, b) => {
		void match;
		return (r + r + g + g + b + b);
	});
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return (result ? {
			r: parseInt(result[1] || '0', 16),
			g: parseInt(result[2] || '0', 16),
			b: parseInt(result[3] || '0', 16)
		} : null
	);
};
type RGBArray = [number, number, number];
type RGBAArray = [number, number, number, number];
export type TColorRGBCoefArray = (
	RGBArray |
	RGBAArray
	);

export function colorRGBCoefArrayToRGB8BitsArray(color: TColorRGBCoefArray) {
	const colors = (<[number, number, number]>color.slice(0, 3)).map(x => Math.round(x * 255));
	if (color.length === 4) { // rgba
		colors.push(color[3]);
	}
	return (colors);
};

export function colorRGBCoefArrayToRGB8BitsString<
	T extends TColorRGBCoefArray
>(
	color: T
): T extends RGBAArray ? `rgba(${number}, ${number}, ${number}, ${number})` : `rgb(${number}, ${number}, ${number})` {
	const colors = colorRGBCoefArrayToRGB8BitsArray(color);
	if (color.length === 4) {
		return <any>`rgba(${colors.join(', ')})`;
	} else {
		return <any>`rgb(${colors.join(', ')})`;
	}
};

export function computeChrominance(color: any) {
	if (
		(typeof color !== 'string') ||
		(color[0] !== '#')
	) {
		throw E.user('Unsupported color type!', {color});
	}
	const rgb = colorHexToRGB(color);
	if (!rgb) {
		throw E.user('Invalid color!', {color});
	}
	const chrominance = (
		(0.2126 * (rgb.r / 255)) +
		(0.7152 * (rgb.g / 255)) +
		(0.0722 * (rgb.b / 255))
	);
	return (chrominance);
};
export const transparentBase64PNGPixel = (
	'data:image/png;base64,iVBORw0KGg' +
	'oAAAANSUhEUgAAAAEAAAABCAQAAAC1HA' +
	'wCAAAAC0lEQVR42mP8Xw8AAoMBgDTD2q' +
	'gAAAAASUVORK5CYII='
);

export abstract class Color<TParsed> {
	static types: TColorTypes[] = [];

	static match(color: string): unknown | null {
		void color;
		throw E.user('Base method!', {});
	};

	static create(color: string) {
		type TParsed = Exclude<ReturnType<TColorTypes['match']>, null>;
		const [Type, parsed] = (
			this.types.map(x => [x, x.match(color)] as const).
			find((x): x is [TColorTypes, TParsed] => !!x[1]) || [null, null]
		);
		if (!Type) {
			throw E.user(`Unknown color<${color}>!`, {color});
		}
		return new Type(parsed);
	};

	abstract toCSS(): string;

	protected constructor(color: TParsed) {
		void color;
	};
};

export interface IRGBAColor {
	r: number;
	g: number;
	b: number;
	a: number;
	hasAlpha: boolean;
};

export class ColorHex extends Color<IRGBAColor> {
	static override match(color: string): IRGBAColor | null {
		const s = color.length;
		if (
			(color[0] !== '#') ||
			!(s === 4 || s === 5 || s === 7 || s === 9)
		) {
			return null;
		}
		color = color.slice(1);
		const rgbaRaw = (
			(s === 4) ? [color[0]!, color[1]!, color[2]!, null] :
				(s === 5) ? [color[0]!, color[1]!, color[2]!, color[3]!] :
					(s === 7) ? [color.slice(0, 2), color.slice(2, 4), color.slice(4, 6), null] :
						[color.slice(0, 2), color.slice(2, 4), color.slice(4, 6), color.slice(6, 8)]
		);
		const rgba = rgbaRaw.map(x => x !== null ? parseInt(x, 16) : null);
		if (Number.isNaN(rgba[0]) || Number.isNaN(rgba[1]) || Number.isNaN(rgba[2]) || Number.isNaN(rgba[3])) {
			return null;
		}
		return {
			r: <number>rgba[0],
			g: <number>rgba[1],
			b: <number>rgba[2],
			a: <number>rgba[3] || 0,
			hasAlpha: rgba[3] === null
		};
	};

	r!: number;
	g!: number;
	b!: number;
	a!: number;
	hasAlpha!: boolean;

	toCSS(): string {
		if (this.hasAlpha) {
			return `#${this.r.toString(16)}${this.g.toString(16)}${this.b.toString(16)}${this.a.toString(16)}`;
		} else {
			return `#${this.r.toString(16)}${this.g.toString(16)}${this.b.toString(16)}`;
		}
	};

	constructor(color: IRGBAColor) {
		super(color);
		Object.assign(this, color);
	};
};
Color.types.push(ColorHex);

export class ColorRGB extends Color<IRGBAColor> {
	static override match(color: string): IRGBAColor | null {
		if (!color.startsWith('rgb') || !color.endsWith(')')) {
			return null;
		}
		color = color.slice(3, -1);
		const hasAlpha = color[0] === 'a';
		if (hasAlpha) {
			color = color.slice(1);
		}
		if (color[0] !== '(') {
			return null;
		}
		color = color.slice(1);
		const rgba = color.split(',').map(x => Number.parseInt(x.trim()));
		if ((hasAlpha && rgba.length !== 4) || (!hasAlpha && rgba.length !== 3)) {
			return null;
		}
		if (Number.isNaN(rgba[0]) || Number.isNaN(rgba[1]) || Number.isNaN(rgba[2]) || Number.isNaN(rgba[3])) {
			return null;
		}
		return {
			r: <number>rgba[0],
			g: <number>rgba[1],
			b: <number>rgba[2],
			a: <number>rgba[3] || 0,
			hasAlpha
		};
	};

	r!: number;
	g!: number;
	b!: number;
	a!: number;
	hasAlpha!: boolean;

	toCSS(): string {
		if (this.hasAlpha) {
			return <any>`rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`;
		} else {
			return <any>`rgb(${this.r}, ${this.g}, ${this.b})`;
		}
	};

	constructor(color: IRGBAColor) {
		super(color);
		Object.assign(this, color);
	};
};
Color.types.push(ColorRGB);

export type TColor = ColorHex | ColorRGB;
export type TColorTypes = typeof ColorHex | typeof ColorRGB;
